/**
 * device-fp.js — hardware/OS-level device fingerprint (browser only).
 *
 * Survives IP change; partially survives browser change. Zero network, ~1-3ms,
 * SSR-safe. Uses ONLY OS/hardware signals (installed fonts, screen+colorDepth,
 * CPU cores, deviceMemory, maxTouchPoints, platform). No canvas/audio/WebGL hash
 * — those are browser-specific and would kill cross-browser matching.
 *
 * The id math lives in the SHARED ./fp-hash.js (byte-identical on the server) so
 * the backend can re-derive the same ids from the posted signals.
 *
 * Exports:
 *   deviceFingerprint(opts)  -> Promise<Result>   (recommended; awaits fonts.ready)
 *   deviceFingerprintSync()  -> Result | null
 *   clearCache()
 */

import { buildIds, coreBucket, FP_VERSION } from './fp-hash.js';

const CACHE_KEY = `__dfp_v${FP_VERSION}`;
const EPS = 0.1; // sub-pixel noise tolerance on text width

/* ------------------------------------------------------------------ */
/* Font table — script-tagged so each font is probed with glyphs it    */
/* actually contains (avoids false positives from per-glyph fallback). */
/* ------------------------------------------------------------------ */

const LATIN_FONTS = [
  // Windows
  'Calibri', 'Cambria', 'Candara', 'Consolas', 'Constantia', 'Corbel',
  'Franklin Gothic Medium', 'Gabriola', 'Impact', 'Lucida Console', 'Marlett',
  'Microsoft Sans Serif', 'MS Gothic', 'Segoe UI', 'Segoe Print', 'Sylfaen',
  'Tahoma', 'Trebuchet MS', 'Verdana', 'Bahnschrift', 'Ink Free',
  // macOS / iOS
  'Apple Chancery', 'Avenir', 'Avenir Next', 'Chalkduster', 'Helvetica Neue',
  'Hoefler Text', 'Menlo', 'Monaco', 'Optima', 'Papyrus', 'Skia', 'Zapfino',
  'American Typewriter', 'Baskerville', 'Didot', 'Futura', 'Gill Sans',
  // Linux
  'DejaVu Sans', 'DejaVu Serif', 'Liberation Sans', 'Liberation Serif',
  'Nimbus Roman', 'Ubuntu', 'Ubuntu Mono', 'Cantarell', 'FreeSans', 'Oxygen',
  // Android
  'Roboto', 'Roboto Condensed', 'Noto Sans', 'Droid Sans', 'Droid Serif',
  // Common installed-by-app fonts (good discriminators)
  'Fira Code', 'JetBrains Mono', 'Source Code Pro', 'Inter', 'Lato',
  'Open Sans', 'Montserrat', 'Poppins', 'Cascadia Code', 'Iosevka',
];

const BANGLA_FONTS = [
  'SolaimanLipi', 'Nikosh', 'NikoshBAN', 'Kalpurush', 'Siyam Rupali',
  'Vrinda', 'Shonar Bangla', 'Noto Sans Bengali', 'Mukti', 'Bangla MN',
];

const CJK_FONTS = ['MS Mincho', 'Meiryo', 'SimSun', 'Microsoft YaHei', 'Malgun Gothic', 'Hiragino Sans'];

const PROBES = {
  latin: ['mmMwWLliI0Oo', 'gjpqyQRSZ@#%&8'],
  bangla: ['অআকখগবভম'],
  cjk: ['水明高測試'],
};

const BASES = ['monospace', 'sans-serif', 'serif'];

/* ------------------------------------------------------------------ */
/* Font detection                                                      */
/* ------------------------------------------------------------------ */

/**
 * Families the page itself loaded via @font-face. Canvas resolves these, so
 * without this filter every visitor of a site using Google Fonts would look like
 * they have "Roboto" installed — silently destroying that signal.
 */
function declaredFamilies() {
  const set = new Set();
  try {
    document.fonts.forEach((ff) => {
      set.add(String(ff.family).replace(/["']/g, '').trim().toLowerCase());
    });
  } catch { /* FontFaceSet unavailable */ }
  return set;
}

function measureFonts(ctx) {
  const declared = declaredFamilies();
  const groups = [
    [LATIN_FONTS, PROBES.latin],
    [BANGLA_FONTS, PROBES.bangla],
    [CJK_FONTS, PROBES.cjk],
  ];

  // baseline[probe][base] = width
  const baseline = {};
  for (const [, probes] of groups) {
    for (const p of probes) {
      if (baseline[p]) continue;
      baseline[p] = {};
      for (const base of BASES) {
        ctx.font = `72px ${base}`;
        baseline[p][base] = ctx.measureText(p).width;
      }
    }
  }

  const found = [];
  let skipped = 0;

  for (const [list, probes] of groups) {
    for (const name of list) {
      if (declared.has(name.toLowerCase())) { skipped++; continue; }
      const quoted = `"${name}"`;
      let present = false;
      outer:
      for (const p of probes) {
        for (const base of BASES) {
          ctx.font = `72px ${quoted},${base}`;
          if (Math.abs(ctx.measureText(p).width - baseline[p][base]) > EPS) {
            present = true;
            break outer;
          }
        }
      }
      if (present) found.push(name);
    }
  }

  return { fonts: found.sort(), skipped, baseline };
}

/**
 * Brave / Tor / hardened builds randomise canvas + text metrics per call.
 * Measuring the same string twice detects that: identical inputs must give
 * identical widths on an honest engine.
 */
function detectFarbling(ctx) {
  const probe = PROBES.latin[0];
  for (const base of BASES) {
    ctx.font = `72px ${base}`;
    const a = ctx.measureText(probe).width;
    ctx.font = `72px ${base}`;
    const b = ctx.measureText(probe).width;
    if (a !== b) return true;
  }
  return false;
}

const safe = (fn, fallback = null) => { try { return fn(); } catch { return fallback; } };

/* ------------------------------------------------------------------ */
/* Main                                                                */
/* ------------------------------------------------------------------ */

function compute() {
  const t0 = performance.now();

  const canvas = safe(() => document.createElement('canvas'));
  const ctx = canvas && safe(() => canvas.getContext('2d'));
  if (!ctx) return null;

  const farbled = safe(() => detectFarbling(ctx), false);
  const { fonts, skipped } = safe(() => measureFonts(ctx), { fonts: [], skipped: 0 });

  const w = safe(() => screen.width, 0) || 0;
  const h = safe(() => screen.height, 0) || 0;
  const long = Math.max(w, h);
  const short = Math.min(w, h);

  const rawCores = safe(() => navigator.hardwareConcurrency, 0) || 0;

  const signals = {
    v: FP_VERSION,
    screen: `${long}x${short}`,
    depth: safe(() => screen.colorDepth, 0) || 0,
    cores: coreBucket(rawCores),
    coresRaw: rawCores,
    memory: safe(() => navigator.deviceMemory, 0) || 0,     // Chromium only
    touch: safe(() => navigator.maxTouchPoints, 0) || 0,
    platform: safe(() => navigator.userAgentData?.platform) || safe(() => navigator.platform) || '',
    fonts,
    // advisory only — browser zoom mutates this, so it never enters an id
    dpr: Math.round((safe(() => window.devicePixelRatio, 1) || 1) * 100) / 100,
  };

  /* --- flags & confidence --- */
  const flags = [];
  if (farbled) flags.push('farbled');
  if (!fonts.length) flags.push('no_fonts');
  if (fonts.length < 6) flags.push('low_fonts');
  if (!signals.memory) flags.push('no_memory_api');
  if (!rawCores) flags.push('no_cores_api');
  if (!Number.isInteger(rawCores) || rawCores > 128) flags.push('cores_implausible');
  if (!long || !Number.isInteger(long)) flags.push('screen_implausible');
  if (safe(() => navigator.webdriver, false)) flags.push('automation');
  if (safe(() => !!navigator.brave, false)) flags.push('brave');
  if (skipped > 0) flags.push(`webfont_excluded:${skipped}`);

  let confidence = 1;
  if (farbled) confidence -= 0.55;
  if (!fonts.length) confidence -= 0.35;
  else if (fonts.length < 6) confidence -= 0.15;
  if (!signals.memory) confidence -= 0.08;
  if (!rawCores) confidence -= 0.08;
  if (flags.includes('cores_implausible') || flags.includes('screen_implausible')) confidence -= 0.3;
  confidence = Math.max(0, Math.round(confidence * 100) / 100);

  // ids come from the SHARED module so the server derives the same values
  const { coreId, fullId, fontSim, bands } = buildIds(signals);

  return {
    coreId,
    fullId,
    fontSim,
    bands,
    signals,
    confidence,
    flags,
    tookMs: Math.round((performance.now() - t0) * 100) / 100,
  };
}

/* ------------------------------------------------------------------ */
/* Cache — recompute only when the cheap signals actually moved         */
/* ------------------------------------------------------------------ */

let memo = null;

function readCache() {
  try {
    const raw = sessionStorage.getItem(CACHE_KEY);
    if (!raw) return null;
    const c = JSON.parse(raw);
    if (c?.signals?.v !== FP_VERSION) return null;
    const w = Math.max(screen.width, screen.height);
    const s = Math.min(screen.width, screen.height);
    if (c.signals.screen !== `${w}x${s}`) return null;   // monitor changed
    if (c.signals.coresRaw !== (navigator.hardwareConcurrency || 0)) return null;
    return c;
  } catch { return null; }
}

const writeCache = (r) => { try { sessionStorage.setItem(CACHE_KEY, JSON.stringify(r)); } catch {} };

function withTimeout(promise, ms) {
  return Promise.race([promise, new Promise((r) => setTimeout(r, ms))]);
}

/** Recommended entry point. Waits for font loading to settle first. */
export async function deviceFingerprint({ cache = true } = {}) {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  if (cache && memo) return memo;
  if (cache) { const c = readCache(); if (c) { memo = c; return c; } }

  // If the page is still loading @font-face families, declaredFamilies() would
  // miss them and we'd report webfonts as installed. Wait, but never hang.
  await withTimeout(safe(() => document.fonts.ready, Promise.resolve()) || Promise.resolve(), 800);

  const r = compute();
  if (r && cache) { memo = r; writeCache(r); }
  return r;
}

/** Sync variant — skips the fonts.ready wait. Use only if you must. */
export function deviceFingerprintSync() {
  if (typeof window === 'undefined' || typeof document === 'undefined') return null;
  if (memo) return memo;
  const r = compute();
  if (r) { memo = r; writeCache(r); }
  return r;
}

export function clearCache() {
  memo = null;
  try { sessionStorage.removeItem(CACHE_KEY); } catch {}
}

export default deviceFingerprint;
